import { memo, useEffect, useMemo, useState, useTransition } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useLocation, useNavigate, useParams } from "@tanstack/react-router"
import { Command as CommandPrimitive } from "cmdk"
import { format } from "date-fns"
import {
	IconAddressBookFill18 as AddressBook,
	IconArrowsLeftRightTrailFill18 as ArrowsLeftRight,
	IconArrowsOppositeDirectionYFill18 as ArrowsOppositeY,
	IconCircleArrowUpFill18 as ArrowUpCircle,
	IconBookOpen4Fill18 as BookOpen,
	IconAtom2Fill18 as Bot,
	IconChartActivityFill18 as ChartActivity,
	IconCheckFill18 as Check,
	IconBoltLightningFill18 as BoltLightning,
	IconArrowRotateAnticlockwiseCheckFill18 as ArrowRotateCheck,
	IconClockFill18 as Clock,
	IconCode2Fill18 as Code2,
	IconConnectedDots2Fill18 as ConnectedDots,
	IconArrowCornerUpLeftFill18 as CornerUpLeft,
	IconCreditCardFill18 as CreditCard,
	IconCubeFill18 as Cube,
	IconDatabaseFill18 as DatabaseZap,
	IconFingerprintFill18 as Fingerprint,
	IconFireFill18 as Fire,
	IconHistoryFill18 as History,
	IconInboxArrowUpFill18,
	IconInboxFill18,
	IconImportFill18 as Import,
	IconLanguageFill18 as Language,
	IconLayers2Fill18 as Layers2,
	IconLifeRingFill18 as LifeBuoy,
	IconLinkFill18 as LinkIcon,
	IconRectLogoutFill18 as LogOut,
	IconEnvelopeFill18 as Mail,
	IconEnvelopePlusFill18 as MailPlus,
	IconEnvelopeBanFill18 as MailX,
	IconMarkdownFill18 as Markdown,
	IconStackPerspectiveFill18 as StackPerspective,
	IconMsgFill18 as MessageCircleIcon,
	IconMessageSparkleFill18 as MessageSparkle,
	IconMsgsFill18 as Msgs,
	IconNotificationFill18 as NotificationIcon,
	IconOffice3Fill18 as Office3,
	IconOrbitSparkleFill18 as OrbitSparkle,
	IconPaperclipFill18 as Paperclip,
	IconQuoteFill18 as Quote,
	IconReceiptFill18 as Receipt,
	IconArrowsRotateCenterFill18 as RefreshCcw,
	IconShapes2Fill18 as Shapes2,
	IconSquaresConnectedFill18 as SquaresConnected,
	IconTableFill18 as Table,
	IconTagFill18 as Tag,
	IconTagPlusFill18 as TagPlus,
	IconDarkLightFill18 as ThemeIcon,
	IconUserArrowRightFill18 as UserArrowRight,
	IconUserChangeFill18 as UserChange,
	IconUserCheckFill18 as UserCheck,
	IconUserClockFill18 as UserClock,
	IconUserPlusFill18 as UserPlus,
	IconUsersFill18 as Users,
	IconUserSettingsFill18 as UserSettings,
	IconUserXmarkFill18 as UserX,
	IconWindow2Fill18 as Window2,
	IconWindowUserFill18 as WindowUser,
	IconXmarkFill18 as X,
} from "nucleo-ui-fill-18"
import {
	IconClockSnoozeOutline18,
	IconMagnifierOutline18 as Search,
	IconGearOutline18 as Settings,
} from "nucleo-ui-outline-18"

import type { BrandfetchData } from "@productlane/api/src/browser"
import { EmailContactSchema } from "@productlane/api/src/browser"
import { normalizeEmail } from "@productlane/api/src/lib/contact"
import { cn, generateId } from "@productlane/lib/src/browser"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	Avatar,
	Badge,
	InboxCheckIcon,
	InboxDotIcon,
	InboxXmarkIcon,
	IssueStatusIcon,
	LucideIcon,
	NewRequestIcon,
	Shortcut,
	SlaIcon,
} from "@productlane/ui"
import {
	Command,
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { ClaudeIcon } from "@productlane/ui/src/icons/project/claude-icon"
import { mutators } from "@productlane/zero/mutators"
import { queries, threadDetailQueryArgs } from "@productlane/zero/queries"

import type { CmdkPage } from "@/lib/states/cmdk-state"
import type { NavItem } from "@/screens/app/sidebar"
import { useOpenOutOfOffice } from "@/components/out-of-office/use-open-out-of-office"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { buildContinueInClaudeLink } from "@/lib/build-continue-in-claude-link"
import { cmdkFilter } from "@/lib/cmdk-filter"
import {
	askAssistantTarget,
	shouldAskAssistant,
} from "@/lib/cmdk-thread-options"
import { getSlaSuggestionsForInput } from "@/lib/dates"
import { useDetailDrawer } from "@/lib/detail-drawer"
import { getBlockedSenderId, isEmailBlocked } from "@/lib/email-utils"
import { FeedbackOriginIconRecord } from "@/lib/feedback"
import { hasLinearConnection } from "@/lib/linear-connection"
import {
	mergeCompanyEmptyMessage,
	mergeCompanyLabel,
	mergeCompanyTargets,
} from "@/lib/merge-company-cmdk"
import {
	mergeContactEmptyMessage,
	mergeContactLabel,
	mergeContactTargets,
} from "@/lib/merge-contact-cmdk"
import { openDeepLink } from "@/lib/open-deep-link"
import { getProjectIcon } from "@/lib/projects"
import { useSession } from "@/lib/session"
import { hasSla as computeHasSla, SLA_DURATIONS } from "@/lib/sla-utils"
import { useCmdkState } from "@/lib/states/cmdk-state"
import { useCopilotState } from "@/lib/states/copilot-state"
import { useSwitchWorkspace } from "@/lib/use-switch-workspace"
import { useRole } from "@/lib/useRole"
import {
	sortWorkspacesByName,
	workspaceDigit,
	workspaceForDigit,
} from "@/lib/workspace-switcher"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { CompanyLogo } from "@/screens/app/customers/company-logo"
import { NOTIFICATIONS_COPY as notificationCopy } from "@/screens/app/notifications/copy"
import { useNotificationCommands } from "@/screens/app/notifications/notification-commands"
import { CreateProjectDialog } from "@/screens/app/projects/create-project-dialog"
import { NAV_ITEMS, useThreadSidebarState } from "@/screens/app/sidebar"
import { useSnoozeOpener } from "@/screens/app/threads/components/center-content/snooze-opener"
import { useSidebarAiChatState } from "@/screens/app/threads/components/right-bar/ai-chat/sidebar-ai-chat-state"
import { useSlaFeatureEnabled } from "@/screens/app/threads/hooks/use-sla-feature-enabled"
import { visibleSettingsNavItems } from "@/screens/settings/integrations/nav-visibility"
import { useSentryAvailable } from "@/screens/settings/integrations/registry"
import {
	ALL_SETTINGS_NAV_ITEMS,
	ALL_SETTINGS_SUBPAGE_NAV_ITEMS,
} from "@/screens/settings/sidebar"
import {
	AssigneePage,
	CompanyPage,
	ContactPage,
	ImportancePage,
	TagsPage,
	ThreadQuickSetItems,
} from "./cmdk-thread-pages"
import { ErrorBoundary } from "./error-boundary"
import { RoleCheck } from "./shared/role-check"
import { ThreadListItem } from "./thread-list/thread-list-item-compact"
import { toast } from "./toast"

const PAGE_PLACEHOLDERS: Partial<Record<CmdkPage, string>> = {
	SLAs: "Set SLA...",
	Importance: "Set importance to...",
	"Assign to": "Assign to...",
	Tags: "Add or remove tags...",
	Contact: "Change contact to...",
	Company: "Change company to...",
	"Merge into": "Search threads from this contact...",
	"Merge company": "Search companies to merge with...",
	"Merge person": "Search people to merge with...",
	"Switch workspace": "Switch workspace...",
}

// These pages search on the server, so the client filter must not hide its matches.
const SERVER_FILTERED_PAGES = new Set<CmdkPage>([
	"SLAs",
	"Merge into",
	"Merge company",
	"Merge person",
	"Contact",
	"Company",
])

const removeDuplicates = (acc: NavItem[], item: NavItem) =>
	acc.find(({ href }) => item.href === href) ? acc : [...acc, item]

const ALL_NAV_ITEMS: Array<NavItem> = [
	...NAV_ITEMS,
	{
		href: "settings",
		title: "Settings",
		icon: Settings,
	},
	{
		href: "/people",
		title: "People",
		icon: Settings,
	},
].reduce<NavItem[]>(removeDuplicates, [])

export const CommandMenu = memo(() => {
	const {
		isOpen,
		setIsOpen,
		pages,
		page,
		removePage,
		openedOnPage,
		commandText,
		setCommandText,
		isCreateProjectOpen,
		setIsCreateProjectOpen,
	} = useCmdkState()
	const { pathname } = useLocation()
	const params = useParams({ strict: false })

	const notificationCommands = useNotificationCommands((s) => s.commands)
	const route = useMemo(() => {
		return ALL_NAV_ITEMS.find((item) => item.href === pathname)
	}, [pathname])

	const isThreadRoute = pathname.startsWith("/threads/") && params.id
	const [thread] = useQuery(
		isThreadRoute && params.id
			? queries.threadWithFullDetails(threadDetailQueryArgs(params.id))
			: queries.threadWithFullDetails(threadDetailQueryArgs("")),
		{ ttl: "none" },
	)
	const company = useCurrentCompany()
	const navigate = useNavigate()
	const { role } = useRole()
	const canAskAssistant = role !== "VIEWER"

	const askAssistant = (text: string) => {
		const target = askAssistantTarget({
			threadId: isThreadRoute && thread ? thread.id : null,
			pathname,
		})
		setIsOpen(false)
		if (target.kind === "thread") {
			const sidebar = useThreadSidebarState.getState()
			if (text) useSidebarAiChatState.getState().setPendingPrompt(text)
			sidebar.setActiveTab("ai-chat")
			useCopilotState
				.getState()
				.requestFocus({ kind: "thread", threadId: target.threadId })
			sidebar.open()
		} else {
			useCopilotState
				.getState()
				.openWith({ kind: "workspace" }, text || undefined)
			if (target.navigateTo) void navigate({ to: target.navigateTo })
		}
		analytics.track("cmdk-ask-assistant", {
			threadId: target.kind === "thread" ? target.threadId : undefined,
			withPrompt: !!text,
		})
	}

	useShortcut(
		"meta+k, ctrl+k",
		() => {
			if (!isOpen) {
				analytics.track("open-cmdk")
			}
			setIsOpen(!isOpen)
		},
		{
			enableOnContentEditable: true,
			enableOnFormTags: true,
			preventDefault: true,
		},
	)
	const [_, startTransition] = useTransition()
	const [inputText, setInputText] = useState(commandText)

	// Sync inputText when commandText is cleared (e.g. on page navigation)
	useEffect(() => {
		if (commandText === "") {
			setInputText("")
		}
	}, [commandText])

	const showContextBadges =
		page !== "Switch workspace" &&
		!!(
			route ||
			notificationCommands?.current ||
			pages.length > 0 ||
			(isThreadRoute && thread) ||
			company
		)

	return (
		<>
			<CreateProjectDialog
				isOpen={isCreateProjectOpen}
				onOpenChange={setIsCreateProjectOpen}
			/>
			<CommandDialog open={isOpen} onOpenChange={setIsOpen}>
				<Command
					className="[&_[cmdk-group-heading]]:pl-3.5 [&_[cmdk-input]]:pl-3 [&_[cmdk-item]]:pl-3.5"
					shouldFilter={!page || !SERVER_FILTERED_PAGES.has(page)}
					filter={cmdkFilter}
					onKeyDown={(e) => {
						if (shouldAskAssistant(e, { page, canAsk: canAskAssistant })) {
							e.preventDefault()
							askAssistant(inputText.trim())
							return
						}
						// Escape goes to previous page
						// Backspace goes to previous page when search is empty
						// but first check whether we are at root to allow esc to close the dialog
						if (page) {
							// Let the dialog close itself when a shortcut opened it on this page.
							if (e.key === "Escape" && openedOnPage && pages.length === 1) {
								return
							}
							if (
								e.key === "Escape" ||
								(e.key === "Backspace" && !commandText)
							) {
								e.preventDefault()
								removePage()
							}
						}
					}}
				>
					{showContextBadges && (
						<div className="flex flex-wrap gap-2 px-4 pt-3">
							{route && (
								<Badge
									variant="dark-gray"
									className="bg-gray-100 dark:bg-gray-600/70"
								>
									{route.title}
								</Badge>
							)}
							{notificationCommands?.current && (
								<Badge
									variant="dark-gray"
									className="bg-gray-100 dark:bg-gray-600/70"
								>
									<span className="text-tertiary">Notification</span>
									<span className="h-1 w-1 flex-none rounded-full bg-gray-400" />
									<span className="max-w-xs truncate">
										{notificationCommands.current.title}
									</span>
								</Badge>
							)}
							{isThreadRoute && thread && (
								<Badge
									variant="dark-gray"
									className="bg-gray-100 dark:bg-gray-600/70"
								>
									{thread.title || "Thread"}
								</Badge>
							)}
							{company && (
								<Badge
									variant="dark-gray"
									className="bg-gray-100 dark:bg-gray-600/70"
								>
									{company.name}
								</Badge>
							)}
							{pages.length > 0
								? [...new Set(pages)].map((page) => (
										<Badge
											variant="dark-gray"
											className="bg-gray-100 dark:bg-gray-600/70"
											key={page}
										>
											{page}
										</Badge>
									))
								: null}
						</div>
					)}

					<CommandInput
						value={inputText}
						onValueChange={(newValue) => {
							setInputText(newValue)
							startTransition(() => {
								setCommandText(newValue)
							})
						}}
						placeholder={
							(page && PAGE_PLACEHOLDERS[page]) ?? "Type a command or search..."
						}
						endAdornment={
							!page && canAskAssistant ? (
								<>
									<span className="text-sm text-tertiary">Ask AI</span>
									<Shortcut variant="default" live={false}>
										Tab
									</Shortcut>
								</>
							) : undefined
						}
						// size="lg"
						className="px-1.5 py-5"
					/>

					<CommandList className="no-scrollbar">
						{page !== "Merge into" &&
							page !== "Merge company" &&
							page !== "Merge person" && (
								<CommandEmpty>No results found.</CommandEmpty>
							)}
						{!page && (
							<>
								<NotificationContextualActions />
								<ThreadContextualActions />
								<ThreadQuickSetItems />
								<ContactContextualActions />
								<CompanyContextualActions />
								<ActionItems />
								<NavigationItems />
								{commandText.length >= 2 && (
									<>
										<SearchThreads withHeading />
										<SearchDocuments withHeading />
										<SearchCompanies withHeading />
										<SearchContacts withHeading />
										<SearchProjects withHeading />
										<SearchIssues withHeading />
										<SearchTags withHeading />
									</>
								)}
							</>
						)}

						{page === "Customers" && (
							<ErrorBoundary boundary="cmdk" size="compact">
								<SearchCompanies />
							</ErrorBoundary>
						)}

						{page === "People" && (
							<ErrorBoundary boundary="cmdk" size="compact">
								<SearchContacts />
							</ErrorBoundary>
						)}

						{page === "SLAs" && <SlaOptions />}

						{page === "Importance" && <ImportancePage />}

						{page === "Assign to" && <AssigneePage />}

						{page === "Tags" && <TagsPage />}

						{page === "Contact" && (
							<ErrorBoundary boundary="cmdk" size="compact">
								<ContactPage />
							</ErrorBoundary>
						)}

						{page === "Company" && (
							<ErrorBoundary boundary="cmdk" size="compact">
								<CompanyPage />
							</ErrorBoundary>
						)}

						{page === "SpawnAgent" && (
							<ErrorBoundary boundary="cmdk" size="compact">
								<SpawnAgentIssueList />
							</ErrorBoundary>
						)}

						{page === "OpenInClaudeCode" && (
							<ErrorBoundary boundary="cmdk" size="compact">
								<OpenInClaudeCodeIssueList />
							</ErrorBoundary>
						)}

						{page === "GenerateDocs" && (
							<ErrorBoundary boundary="cmdk" size="compact">
								<GenerateDocsIssueList />
							</ErrorBoundary>
						)}

						{page === "DocGaps" && (
							<ErrorBoundary boundary="cmdk" size="compact">
								<DocGapsDaysList />
							</ErrorBoundary>
						)}

						{page === "Merge into" && (
							<ErrorBoundary boundary="cmdk" size="compact">
								<MergeThreadOptions />
							</ErrorBoundary>
						)}

						{page === "Merge company" && (
							<ErrorBoundary boundary="cmdk" size="compact">
								<MergeCompanyOptions />
							</ErrorBoundary>
						)}

						{page === "Merge person" && (
							<ErrorBoundary boundary="cmdk" size="compact">
								<MergeContactOptions />
							</ErrorBoundary>
						)}

						{page === "Switch workspace" && (
							<ErrorBoundary boundary="cmdk" size="compact">
								<SwitchWorkspaceOptions />
							</ErrorBoundary>
						)}
					</CommandList>
				</Command>
			</CommandDialog>
		</>
	)
})

interface SubItemProps {
	withHeading?: boolean
}

const SearchProjects = memo(({ withHeading = false }: SubItemProps) => {
	const { open: openDetail } = useDetailDrawer()
	const { setIsOpen, commandText } = useCmdkState()
	const [projects] = useQuery(
		queries.searchProjects({ query: commandText, limit: 20 }),
		{ ttl: "none" },
	)

	return (
		<CommandGroup heading={withHeading ? "Projects" : undefined}>
			{projects.map((project) => {
				const ProjectIcon = getProjectIcon(project.icon)
				return (
					<CommandItem
						size="lg"
						key={project.id}
						value={`${project.name}-${project.id}`}
						Icon={() => (
							<ProjectIcon
								className="h-4 w-4"
								style={{ color: project.color ?? "" }}
							/>
						)}
						onSelect={() => {
							openDetail("project", project.id)
							setIsOpen(false)
							analytics.track("go-to-project", { cmdk: true, id: project.id })
						}}
					>
						{project.name}
					</CommandItem>
				)
			})}
		</CommandGroup>
	)
})

const SearchIssues = memo(({ withHeading = false }: SubItemProps) => {
	const { open: openDetail } = useDetailDrawer()
	const { setIsOpen, commandText } = useCmdkState()
	const [issues] = useQuery(
		queries.searchIssues({ query: commandText, limit: 20 }),
		{ ttl: "none" },
	)

	return (
		<CommandGroup heading={withHeading ? "Issues" : undefined}>
			{issues.map((issue) => (
				<CommandItem
					size="lg"
					key={issue.id}
					value={`${issue.title}-${issue.id}`}
					Icon={() => (
						<IssueStatusIcon
							className="h-3.5 w-3.5"
							status={issue.status}
							color={issue.statusColor ?? ""}
						/>
					)}
					onSelect={() => {
						openDetail("issue", issue.id)
						setIsOpen(false)
						analytics.track("go-to-issue", { cmdk: true, id: issue.id })
					}}
				>
					{issue.title}
				</CommandItem>
			))}
		</CommandGroup>
	)
})

const SearchContacts = memo(({ withHeading = false }: SubItemProps) => {
	const { setIsOpen, commandText } = useCmdkState()
	const [contacts] = useQuery(
		queries.searchContacts({ query: commandText, limit: 20 }),
		{ ttl: "none" },
	)

	const navigate = useNavigate()
	return (
		<CommandGroup heading={withHeading ? "People" : undefined}>
			{contacts.map((contact) => (
				<CommandItem
					size="lg"
					key={contact.id}
					value={
						contact.name
							? `${contact.name} (${contact.email})-${contact.id}`
							: `${contact.email}-${contact.id}`
					}
					onSelect={() => {
						void navigate({ to: "/people/$id", params: { id: contact.id } })
						setIsOpen(false)
						analytics.track("go-to-contact", { cmdk: true, id: contact.id })
					}}
				>
					{contact.name ? `${contact.name} (${contact.email})` : contact.email}
				</CommandItem>
			))}
		</CommandGroup>
	)
})

const SearchTags = memo(({ withHeading = false }: SubItemProps) => {
	const { setIsOpen, commandText } = useCmdkState()
	const [tags] = useQuery(
		queries.searchTags({ query: commandText, limit: 20 }),
		{ ttl: "none" },
	)
	const [tagGroups] = useQuery(queries.tagGroups())

	const tagGroupColorMap = useMemo(() => {
		const map = new Map<string, string>()
		tagGroups.forEach((group) => {
			map.set(group.id, group.color)
		})
		return map
	}, [tagGroups])

	const navigate = useNavigate()
	return (
		<CommandGroup heading={withHeading ? "Tag" : undefined}>
			{tags.map((tag) => {
				const TagIcon = getProjectIcon(tag.icon)
				const groupColor = tagGroupColorMap.get(tag.tagGroupId)
				return (
					<CommandItem
						size="lg"
						key={tag.id}
						value={`${tag.name}-${tag.id}`}
						Icon={() => (
							<TagIcon className="h-4 w-4" style={{ color: groupColor }} />
						)}
						onSelect={() => {
							void navigate({ to: "/tags/$id", params: { id: tag.id } })
							setIsOpen(false)
							analytics.track("go-to-opportunity", {
								cmdk: true,
								id: tag.id,
							})
						}}
					>
						{tag.name}
					</CommandItem>
				)
			})}
		</CommandGroup>
	)
})

const SearchCompanies = memo(({ withHeading = false }: SubItemProps) => {
	const { setIsOpen, commandText } = useCmdkState()
	const [companies] = useQuery(
		queries.searchCompanies({ query: commandText, limit: 20 }),
		{ ttl: "none" },
	)

	const navigate = useNavigate()
	return (
		<CommandGroup heading={withHeading ? "Customers" : undefined}>
			{companies.map((company) => (
				<CommandItem
					size="lg"
					key={company.id}
					value={`${company.name}-${company.id}`}
					Icon={() => (
						<CompanyLogo
							brandfetchData={
								company.companyMeta?.brandfetchData as unknown as BrandfetchData
							}
							fallback={company.name}
							linearLogoUrl={company?.logoUrl || undefined}
							size="xs"
						/>
					)}
					onSelect={() => {
						void navigate({ to: "/customers/$id", params: { id: company.id } })
						setIsOpen(false)
					}}
				>
					{company.name}
				</CommandItem>
			))}
		</CommandGroup>
	)
})

// Five is the ceiling on purpose: threads outnumber every other entity, so a
// longer list buries the actions and the other search groups.
const THREAD_SEARCH_LIMIT = 5

const SearchThreads = memo(({ withHeading = false }: SubItemProps) => {
	const { setIsOpen, commandText } = useCmdkState()
	const [threads] = useQuery(
		queries.searchThreadTitles({
			query: commandText,
			limit: THREAD_SEARCH_LIMIT,
		}),
		{ ttl: "none" },
	)

	const navigate = useNavigate()
	return (
		<CommandGroup heading={withHeading ? "Threads" : undefined}>
			{threads.map((thread) => {
				const OriginIcon = FeedbackOriginIconRecord[thread.origin]
				const title = thread.title?.trim() || "Untitled thread"
				return (
					<CommandItem
						size="lg"
						key={thread.id}
						value={
							thread.number != null
								? `#${thread.number} ${title}-${thread.id}`
								: `${title}-${thread.id}`
						}
						Icon={() => <OriginIcon className="h-4 w-4" />}
						onSelect={() => {
							void navigate({ to: "/threads/$id", params: { id: thread.id } })
							setIsOpen(false)
							analytics.track("go-to-thread", { cmdk: true, id: thread.id })
						}}
					>
						{thread.number != null && (
							<span className="mr-1.5 text-tertiary">#{thread.number}</span>
						)}
						{title}
					</CommandItem>
				)
			})}
		</CommandGroup>
	)
})

const SearchDocuments = memo(({ withHeading = false }: SubItemProps) => {
	const { setIsOpen, commandText } = useCmdkState()
	const [helpCenterArticles] = useQuery(
		queries.searchHelpCenterArticles({
			query: commandText,
			limit: 20,
		}),
		{ ttl: "none" },
	)

	const navigate = useNavigate()
	return (
		<CommandGroup heading={withHeading ? "Docs" : undefined}>
			{helpCenterArticles.map((article) => {
				const ArticleIcon = () => (
					<LucideIcon
						icon={article.icon}
						className="h-4 w-4"
						style={{ strokeWidth: 1.75 }}
					/>
				)
				return (
					<CommandItem
						size="lg"
						key={article.id}
						value={`${article.title}-${article.id}`}
						Icon={ArticleIcon}
						onSelect={() => {
							void navigate({
								to: "/edit-docs/$id",
								params: { id: article.id },
							})
							setIsOpen(false)
							analytics.track("go-to-document", { cmdk: true, id: article.id })
						}}
					>
						{article.title}
					</CommandItem>
				)
			})}
		</CommandGroup>
	)
})

const ActionItems = memo(() => {
	const { setIsOpen, setCommandText, setIsCreateProjectOpen, addPage } =
		useCmdkState()
	const { session } = useSession()
	const zero = useZero()
	const navigate = useNavigate()
	const workspace = useZeroWorkspace()
	const openOutOfOffice = useOpenOutOfOffice()
	return (
		<>
			<CommandGroup heading="Actions">
				<RoleCheck not={["VIEWER"]}>
					<CommandItem
						size="lg"
						onSelect={async () => {
							if (!session) {
								return
							}
							const id = generateId()
							await mutateOk(
								zero.mutate(
									mutators.threads.create({
										id,
										reporterId: session.userId,
										text: "",
										assigneeId: session.userId,
										origin: "INAPP",
										painLevel: "UNKNOWN",
										state: "NEW",
										contactIsUnverified: false,
										attachments: [],
										replied: false,
										lastMessageAt: new Date().getTime(),
										isRead: true,
									}),
								),
							)
							void navigate({ to: "/threads/$id", params: { id } })
							setIsOpen(false)
							analytics.track("create-insight", { cmdk: true })
						}}
						Icon={NewRequestIcon}
					>
						Create thread
					</CommandItem>
					<CommandItem
						size="lg"
						onSelect={() => {
							setIsCreateProjectOpen(true)
							analytics.track("open-create-project-modal", { cmdk: true })
						}}
						Icon={Cube}
					>
						Create project
					</CommandItem>
					<CommandItem
						size="lg"
						onSelect={() => {
							addPage("DocGaps")
							analytics.track("open-docs-gaps", { cmdk: true })
						}}
						Icon={Bot}
					>
						Improve docs from past conversations
					</CommandItem>
					<CommandItem
						size="lg"
						keywords={["away", "vacation", "holiday", "leave", "cover"]}
						onSelect={() => {
							setIsOpen(false)
							setCommandText("")
							openOutOfOffice()
							analytics.track("open-out-of-office", { cmdk: true })
						}}
						Icon={UserClock}
					>
						Out of office
					</CommandItem>
				</RoleCheck>

				<CommandItem
					size="lg"
					onSelect={async () => {
						await navigator.clipboard.writeText(window.location.href)
						setIsOpen(false)
						setCommandText("")
						analytics.track("copy-current-url", { cmdk: true })
						toast.success({ title: "URL copied to clipboard" })
					}}
					Icon={LinkIcon}
				>
					Copy current URL into clipboard
				</CommandItem>

				<CommandItem
					size="lg"
					onSelect={() => {
						setIsOpen(false)
						setCommandText("")
						// @ts-expect-error Global widget calls
						// eslint-disable-next-line
						window.Productlane.open("feedback")
					}}
					Icon={MessageCircleIcon}
				>
					Send feedback
				</CommandItem>

				<CommandItem
					size="lg"
					onSelect={() => {
						setIsOpen(false)
						setCommandText("")
						// @ts-expect-error Global widget calls
						// eslint-disable-next-line
						window.Productlane.open("feedback")
					}}
					Icon={LifeBuoy}
				>
					Contact support
				</CommandItem>

				{hasLinearConnection(workspace) ? (
					<CommandItem
						size="lg"
						onSelect={() => {
							analytics.track("sync-linear", { cmdk: true })
							void navigate({ to: "/setup-linear" })
							setIsOpen(false)
						}}
						Icon={RefreshCcw}
					>
						Sync Linear
					</CommandItem>
				) : null}

				<CommandItem
					size="lg"
					onSelect={() => {
						analytics.track("reindex-portal-search", { cmdk: true })
						// TODO: fix portal search reindex
						// void zero.mutate(mutators.portalSearch.reindex({})).client
						void zero.mutate(mutators.helpCenterArticles.reindex({})).client
						setIsOpen(false)
						setCommandText("")
						toast.success({
							title: "Reindexing portal search",
							description: "This may take a couple of seconds.",
						})
					}}
					Icon={DatabaseZap}
				>
					Reindex Portal search
				</CommandItem>

				<CommandItem
					size="lg"
					onSelect={() => addPage("Switch workspace")}
					Icon={ArrowsOppositeY}
					shortcuts={[ShortcutSymbols.O, ShortcutSymbols.W]}
				>
					Switch workspace
				</CommandItem>

				<CommandItem
					size="lg"
					onSelect={() => {
						analytics.track("logout", { cmdk: true })
						void navigate({ to: "/logout" })
						setIsOpen(false)
						setCommandText("")
					}}
					Icon={LogOut}
				>
					Log out
				</CommandItem>
			</CommandGroup>
		</>
	)
})

const SwitchWorkspaceOptions = memo(() => {
	const { setIsOpen, commandText } = useCmdkState()
	const { session } = useSession()
	const [unsortedWorkspaces] = useQuery(queries.userWorkspaces())
	const workspaces = useMemo(
		() => sortWorkspacesByName(unsortedWorkspaces),
		[unsortedWorkspaces],
	)
	const { switchTo } = useSwitchWorkspace()

	const select = (workspaceId: string, via: "digit" | "select") => {
		analytics.track("switch-workspace", { cmdk: true, via })
		if (workspaceId === session?.workspaceId) {
			setIsOpen(false)
			return
		}
		switchTo(workspaceId)
	}

	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			// Digits pick a workspace until the user starts typing a search.
			if (commandText || e.metaKey || e.ctrlKey || e.altKey) {
				return
			}
			const target = workspaceForDigit(workspaces, e.key)
			if (!target) {
				return
			}
			e.preventDefault()
			e.stopPropagation()
			select(target.id, "digit")
		}
		window.addEventListener("keydown", onKeyDown, { capture: true })
		return () =>
			window.removeEventListener("keydown", onKeyDown, { capture: true })
	})

	return (
		<CommandGroup heading={session?.email}>
			{workspaces.map((ws, index) => {
				const digit = workspaceDigit(index)
				return (
					<CommandItem
						size="lg"
						key={ws.id}
						value={`${ws.name} ${ws.id}`}
						Icon={() => (
							<Avatar
								name={ws.name}
								image={ws.logoUrl}
								size="xs"
								roundness="md"
								disableColorHash
								fallbackProps={{
									className:
										"bg-gray-950 text-white dark:bg-gray-50 dark:text-gray-950",
								}}
							/>
						)}
						hint={
							ws.id === session?.workspaceId ? (
								<Check className="h-4 w-4 flex-none text-primary" />
							) : undefined
						}
						shortcuts={digit ? [digit] : undefined}
						onSelect={() => select(ws.id, "select")}
					>
						{ws.name}
					</CommandItem>
				)
			})}
		</CommandGroup>
	)
})

const DOC_GAP_DAY_OPTIONS = [7, 14, 30]

const DocGapsDaysList = memo(() => {
	const { setIsOpen, setCommandText } = useCmdkState()
	const analyzeMutation = api.ai.analyzeConversationsForDocsGaps.useMutation()

	const handleSelect = async (days: number) => {
		try {
			await analyzeMutation.mutateAsync({ days })
			toast.success({
				title: "Analyzing conversations for doc gaps",
				description: `Reviewing the last ${days} days. Doc proposals will appear in your review queue.`,
			})
			setIsOpen(false)
			setCommandText("")
			analytics.track("docs-gaps-analyze", { cmdk: true, days })
		} catch (error) {
			toast.error({
				title: "Failed to start analysis",
				description: error instanceof Error ? error.message : "Unknown error",
			})
		}
	}

	return (
		<CommandGroup heading="Analyze conversations from the last...">
			{DOC_GAP_DAY_OPTIONS.map((days) => (
				<CommandItem
					key={days}
					size="lg"
					onSelect={() => handleSelect(days)}
					Icon={Bot}
				>
					{days} days
				</CommandItem>
			))}
		</CommandGroup>
	)
})

const ThreadContextualActions = memo(() => {
	const { pathname } = useLocation()
	const params = useParams({ strict: false })
	const { setIsOpen, addPage } = useCmdkState()
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const slaEnabled = useSlaFeatureEnabled()
	const navigate = useNavigate()
	const { isOpen: sidebarOpen, activeTab } = useThreadSidebarState()
	const openSnooze = useSnoozeOpener((s) => s.open)
	const { session } = useSession()

	const isThreadRoute = pathname.startsWith("/threads/") && params.id
	const [thread] = useQuery(
		isThreadRoute && params.id
			? queries.threadWithFullDetails(threadDetailQueryArgs(params.id))
			: queries.threadWithFullDetails(threadDetailQueryArgs("")),
		{ ttl: "none" },
	)

	const [blockedEmails] = useQuery(queries.blockedSenders())

	const getSenderEmail = useMemo(() => {
		if (!thread || !isThreadRoute) return null

		if (thread.contact?.email) {
			return thread.contact.email
		}

		const inboundEmails = thread.emails?.filter((email) => {
			try {
				const fromContact = EmailContactSchema.parse(email.from)
				return fromContact.email && email.type === "INBOUND"
			} catch {
				return false
			}
		})

		if (inboundEmails && inboundEmails.length > 0) {
			const latestInboundEmail = inboundEmails[0]
			if (latestInboundEmail) {
				try {
					const fromContact = EmailContactSchema.parse(latestInboundEmail.from)
					return fromContact.email
				} catch {
					return null
				}
			}
		}

		return null
	}, [thread, isThreadRoute])

	const isBlocked = useMemo(
		() => isEmailBlocked(getSenderEmail, blockedEmails),
		[getSenderEmail, blockedEmails],
	)

	const blockedSenderId = useMemo(
		() => getBlockedSenderId(getSenderEmail, blockedEmails),
		[getSenderEmail, blockedEmails],
	)

	const handleMarkDone = async () => {
		if (!thread) return

		const newState = thread.state === "PROCESSED" ? "NEW" : "PROCESSED"
		await mutateOk(
			zero.mutate(
				mutators.threads.update({
					id: thread.id,
					state: newState,
					lastStateChangeAtIso: new Date().toISOString(),
				}),
			),
		)

		toast.success({
			title: newState === "PROCESSED" ? "Marked done" : "Moved to Inbox",
		})
		setIsOpen(false)
		analytics.track("mark-thread-done", { cmdk: true, threadId: thread.id })
	}

	const handleBlockSender = async () => {
		if (!thread || !getSenderEmail) return

		if (isBlocked && blockedSenderId) {
			const result = await zero.mutate(
				mutators.blockedSenders.delete({
					id: blockedSenderId,
				}),
			).client

			if (result.type === "success") {
				toast.success({
					title: "Sender unblocked",
					description: `Feedbacks from ${getSenderEmail} will no longer be ignored.`,
					action: {
						label: "View Blocked Senders",
						callback: () => {
							void navigate({ to: "/settings/emails/blocklist" })
						},
					},
				})
				setIsOpen(false)
				analytics.track("unblock-email-sender", {
					cmdk: true,
					threadId: thread.id,
					email: getSenderEmail,
				})
			} else {
				toast.error({
					title: "Failed to unblock sender",
					description:
						result.error instanceof Error
							? result.error.message
							: "Unknown error",
				})
			}
		} else {
			const result = await zero.mutate(
				mutators.blockedSenders.create({
					id: generateId(),
					value: normalizeEmail(getSenderEmail),
					type: "EMAIL",
				}),
			).client

			if (result.type === "success") {
				toast.success({
					title: "Sender blocked",
					description: `Feedbacks from ${getSenderEmail} will be automatically ignored.`,
					action: {
						label: "View Blocked Senders",
						callback: () => {
							void navigate({ to: "/settings/emails/blocklist" })
						},
					},
				})
				setIsOpen(false)
				analytics.track("block-email-sender", {
					cmdk: true,
					threadId: thread.id,
					email: getSenderEmail,
				})
			} else {
				toast.error({
					title: "Failed to block sender",
					description:
						result.error instanceof Error
							? result.error.message
							: "Unknown error",
				})
			}
		}
	}

	const triggerAgent = api.codingAgent.trigger.useMutation()
	const utils = api.useUtils()
	const generateDocsProposalsMutation =
		api.ai.generateDocsProposals.useMutation()

	const handleGenerateDocs = async (issue: {
		id: string
		title: string
		identifier: string | null
	}) => {
		if (!thread) return
		try {
			await generateDocsProposalsMutation.mutateAsync({
				issueIds: [issue.id],
				threadId: thread.id,
			})
			toast.success({
				title: "Documentation proposal started",
				description: `Working on docs for ${issue.identifier ?? issue.title}`,
			})
			setIsOpen(false)
			analytics.track("docs-proposals-generate", {
				cmdk: true,
				threadId: thread.id,
				issueId: issue.id,
			})
		} catch (error) {
			toast.error({
				title: "Failed to start documentation proposal",
				description: error instanceof Error ? error.message : "Unknown error",
			})
		}
	}

	const handleSpawnAgent = async (issue: {
		id: string
		title: string
		identifier: string | null
	}) => {
		if (!thread) return
		try {
			await triggerAgent.mutateAsync({
				issueId: issue.id,
				triggerSource: "cmdk",
			})
			toast.success({
				title: "Productlane Agent spawned",
				description: `Working on ${issue.identifier ?? issue.title}`,
			})
			setIsOpen(false)
			analytics.track("spawn-coding-agent", {
				cmdk: true,
				threadId: thread.id,
				issueId: issue.id,
			})
		} catch (error) {
			if (
				error instanceof Error &&
				"data" in error &&
				(error as { data?: { code?: string } }).data?.code ===
					"PRECONDITION_FAILED"
			) {
				toast.error({
					title: "Cursor Cloud not configured",
					description: error.message,
					action: {
						label: "Configure",
						callback: () => {
							void navigate({ to: "/settings/integrations/cursor" })
							setIsOpen(false)
						},
					},
				})
			} else {
				toast.error({
					title: "Failed to spawn Productlane Agent",
					description: error instanceof Error ? error.message : "Unknown error",
				})
			}
		}
	}

	const handleOpenInClaudeCode = async (issue: {
		id: string
		title: string
		identifier: string | null
	}) => {
		try {
			const { deepLink, branchName, prompt } =
				await utils.codingAgent.buildClaudeCodeLink.fetch({
					issueId: issue.id,
				})
			openDeepLink(deepLink)
			toast.success({
				title: "Opening in Claude Code",
				description: branchName ? `Branch ${branchName}` : undefined,
				descriptionAction: {
					label: "Copy prompt",
					callback: () => void navigator.clipboard.writeText(prompt),
				},
			})
			setIsOpen(false)
			analytics.track("open-issue-in-claude-code", {
				cmdk: true,
				threadId: thread?.id,
				issueId: issue.id,
			})
		} catch (error) {
			if (
				error instanceof Error &&
				"data" in error &&
				(error as { data?: { code?: string } }).data?.code ===
					"PRECONDITION_FAILED"
			) {
				toast.error({
					title: "No repository configured",
					description: error.message,
					action: {
						label: "Configure",
						callback: () => {
							void navigate({ to: "/settings/integrations/cursor" })
							setIsOpen(false)
						},
					},
				})
			} else {
				toast.error({
					title: "Failed to open in Claude Code",
					description: error instanceof Error ? error.message : "Unknown error",
				})
			}
		}
	}

	const handleContinueInClaude = () => {
		if (!thread) return
		const { deepLink, prompt } = buildContinueInClaudeLink({
			messages: useSidebarAiChatState.getState().messages ?? [],
			threadId: thread.id,
			threadTitle: thread.title,
		})
		openDeepLink(deepLink)
		toast.success({
			title: "Opening in Claude Code",
			descriptionAction: {
				label: "Copy prompt",
				callback: () => void navigator.clipboard.writeText(prompt),
			},
		})
		setIsOpen(false)
		analytics.track("continue-thread-in-claude", {
			cmdk: true,
			threadId: thread.id,
		})
	}

	const handleToggleContactSubscribed = async () => {
		if (!thread?.contact) return

		await mutateOk(
			zero.mutate(
				mutators.contacts.update({
					id: thread.contact.id,
					isSubscribed: !thread.contact.isSubscribed,
				}),
			),
		)

		toast.success({
			title: thread.contact.isSubscribed ? "Unsubscribed" : "Subscribed",
			description: thread.contact.isSubscribed
				? "This contact will no longer receive email updates."
				: "This contact will receive email updates.",
		})
		setIsOpen(false)
		analytics.track("toggle-contact-subscribed", {
			cmdk: true,
			origin: "thread-cmdk",
			id: thread.contact.id,
			threadId: thread.id,
			isSubscribed: !thread.contact.isSubscribed,
		})
	}

	const hasSla = useMemo(
		() => computeHasSla(thread, workspace),
		[thread, workspace],
	)

	const handleToggleAssignMe = async () => {
		if (!thread || !session) return
		const unassign = thread.assigneeId === session.userId
		setIsOpen(false)
		await mutateOk(
			zero.mutate(
				mutators.threads.update({
					id: thread.id,
					assigneeId: unassign ? null : session.userId,
				}),
			),
		)
		toast.success({
			title: unassign ? "Unassigned from you" : "Assigned to you",
		})
		analytics.track("assign-thread", {
			cmdk: true,
			threadId: thread.id,
			self: true,
		})
	}

	const handleRemoveSla = async () => {
		if (!thread) return
		await mutateOk(
			zero.mutate(
				mutators.threads.update({
					id: thread.id,
					slaDeadlineAtIso: new Date(0).toISOString(),
				}),
			),
		)
		toast.success({ title: "SLA removed" })
		setIsOpen(false)
	}

	if (!isThreadRoute || !thread) {
		return null
	}

	return (
		<CommandGroup heading="Thread Actions">
			<RoleCheck not={["VIEWER"]}>
				<CommandItem
					size="lg"
					onSelect={handleMarkDone}
					Icon={thread.state === "PROCESSED" ? IconInboxArrowUpFill18 : Check}
					shortcuts={[ShortcutSymbols.E]}
				>
					{thread.state === "PROCESSED" ? "Move to Inbox" : "Mark as done"}
				</CommandItem>

				{thread.state !== "SNOOZED" &&
					thread.state !== "PROCESSED" &&
					openSnooze && (
						<CommandItem
							size="lg"
							Icon={Clock}
							shortcuts={[ShortcutSymbols.H]}
							onSelect={() => {
								setIsOpen(false)
								openSnooze()
								analytics.track("snooze-thread", {
									cmdk: true,
									threadId: thread.id,
								})
							}}
						>
							Snooze thread
						</CommandItem>
					)}
				{thread.state === "SNOOZED" && (
					<CommandItem
						size="lg"
						onSelect={async () => {
							await mutateOk(
								zero.mutate(
									mutators.threads.update({
										id: thread.id,
										state: "UNSNOOZED",
									}),
								),
							)
							toast.success({ title: "Thread unsnoozed" })
							setIsOpen(false)
						}}
						Icon={Clock}
						shortcuts={[ShortcutSymbols.H]}
					>
						Unsnooze thread
					</CommandItem>
				)}
				<CommandItem
					size="lg"
					onSelect={() => addPage("Assign to")}
					Icon={UserArrowRight}
					shortcuts={[ShortcutSymbols.A]}
				>
					Assign to...
				</CommandItem>
				{session && (
					<CommandItem
						size="lg"
						onSelect={handleToggleAssignMe}
						Icon={thread.assigneeId === session.userId ? UserX : UserCheck}
						shortcuts={[ShortcutSymbols.I]}
					>
						{thread.assigneeId === session.userId
							? "Unassign from me"
							: "Assign to me"}
					</CommandItem>
				)}
				<CommandItem
					size="lg"
					onSelect={() => addPage("Importance")}
					Icon={ArrowUpCircle}
					shortcuts={[ShortcutSymbols.P]}
				>
					Set importance...
				</CommandItem>
				<CommandItem
					size="lg"
					onSelect={() => addPage("Tags")}
					Icon={TagPlus}
					shortcuts={[ShortcutSymbols.T]}
				>
					Add tag...
				</CommandItem>
				<CommandItem
					size="lg"
					onSelect={() => addPage("Contact")}
					Icon={UserChange}
				>
					Change contact...
				</CommandItem>
				<CommandItem
					size="lg"
					onSelect={() => addPage("Company")}
					Icon={Office3}
				>
					Change company...
				</CommandItem>
				{thread.contact && (
					<CommandItem
						size="lg"
						onSelect={handleToggleContactSubscribed}
						Icon={thread.contact.isSubscribed ? UserX : UserPlus}
					>
						{thread.contact.isSubscribed
							? "Unsubscribe contact"
							: "Subscribe contact"}
					</CommandItem>
				)}
				{getSenderEmail && (
					<CommandItem
						size="lg"
						onSelect={handleBlockSender}
						Icon={isBlocked ? MailPlus : MailX}
					>
						{isBlocked ? "Unblock sender" : "Block sender"} ({getSenderEmail})
					</CommandItem>
				)}
				{slaEnabled && (
					<CommandItem
						size="lg"
						onSelect={() => addPage("SLAs")}
						Icon={SlaIcon}
					>
						Set SLA...
					</CommandItem>
				)}
				{slaEnabled && hasSla && (
					<CommandItem size="lg" onSelect={handleRemoveSla} Icon={X}>
						Remove SLA
					</CommandItem>
				)}
				{thread.issues &&
					thread.issues.filter((i) => !i.completedAt).length > 0 && (
						<CommandItem
							size="lg"
							onSelect={() => {
								const issues = thread.issues?.filter((i) => !i.completedAt)
								if (!issues || issues.length === 0) return
								if (issues.length === 1) {
									void handleSpawnAgent(issues[0]!)
								} else {
									addPage("SpawnAgent")
								}
							}}
							Icon={Bot}
						>
							Spawn Productlane Agent
						</CommandItem>
					)}
				{thread.issues &&
					thread.issues.filter((i) => !i.completedAt).length > 0 && (
						<CommandItem
							size="lg"
							onSelect={() => {
								const issues = thread.issues?.filter((i) => !i.completedAt)
								if (!issues || issues.length === 0) return
								if (issues.length === 1) {
									void handleOpenInClaudeCode(issues[0]!)
								} else {
									addPage("OpenInClaudeCode")
								}
							}}
							Icon={ClaudeIcon}
						>
							Open in Claude Code
						</CommandItem>
					)}
				{sidebarOpen && activeTab === "ai-chat" && (
					<CommandItem
						size="lg"
						onSelect={handleContinueInClaude}
						Icon={ClaudeIcon}
					>
						Continue in Claude
					</CommandItem>
				)}
				{thread.issues && thread.issues.length > 0 && (
					<CommandItem
						size="lg"
						onSelect={() => {
							const issues = thread.issues
							if (!issues || issues.length === 0) return
							if (issues.length === 1) {
								void handleGenerateDocs(issues[0]!)
							} else {
								addPage("GenerateDocs")
							}
						}}
						Icon={BookOpen}
					>
						Generate documentation proposal
					</CommandItem>
				)}
				<CommandItem
					size="lg"
					onSelect={() => {
						if (!thread.contactId) {
							toast.info({
								title: "Merge needs a contact on this thread",
								description:
									"You can only merge into another thread from the same person.",
								icon: ArrowsLeftRight,
							})
							return
						}
						addPage("Merge into")
						analytics.track("open-merge-thread", {
							cmdk: true,
							threadId: thread.id,
						})
					}}
					Icon={ArrowsLeftRight}
				>
					Merge thread...
				</CommandItem>
			</RoleCheck>
		</CommandGroup>
	)
})

const ContactContextualActions = memo(() => {
	const { pathname } = useLocation()
	const params = useParams({ strict: false })
	const { setIsOpen, addPage } = useCmdkState()
	const zero = useZero()

	const isContactRoute = pathname.startsWith("/people/") && params.id
	const [contact] = useQuery(
		isContactRoute && params.id
			? queries.contactDetails(params.id)
			: queries.contactDetails(""),
		{ ttl: "none" },
	)

	if (!isContactRoute || !contact) {
		return null
	}

	return (
		<CommandGroup heading="Person Actions">
			<RoleCheck not={["VIEWER"]}>
				<CommandItem
					size="lg"
					Icon={Mail}
					onSelect={async () => {
						await mutateOk(
							zero.mutate(
								mutators.contacts.update({
									id: contact.id,
									isSubscribed: !contact.isSubscribed,
								}),
							),
						)

						toast.success({
							title: contact.isSubscribed ? "Unsubscribed" : "Subscribed",
						})

						setIsOpen(false)
						analytics.track("toggle-contact-subscribed", {
							cmdk: true,
							id: contact.id,
							isSubscribed: !contact.isSubscribed,
						})
					}}
				>
					{contact.isSubscribed ? "Unsubscribe contact" : "Subscribe contact"}
				</CommandItem>
				<CommandItem
					size="lg"
					Icon={ArrowsLeftRight}
					onSelect={() => addPage("Merge person")}
				>
					{mergeContactLabel(contact.name || contact.email)}
				</CommandItem>
			</RoleCheck>
		</CommandGroup>
	)
})

const useCurrentContact = () => {
	const { pathname } = useLocation()
	const params = useParams({ strict: false })
	const isContactRoute = pathname.startsWith("/people/") && !!params.id
	const [contact] = useQuery(
		queries.contactDetails(isContactRoute && params.id ? params.id : ""),
		{ ttl: "none" },
	)
	return isContactRoute ? contact : undefined
}

const MergeContactOptions = memo(() => {
	const { openMergeContactDialog, commandText } = useCmdkState()
	const contact = useCurrentContact()

	const [contacts] = useQuery(
		queries.searchContacts({ query: commandText, limit: 20 }),
		{ ttl: "none" },
	)
	const targets = useMemo(
		() => mergeContactTargets(contacts, contact?.id ?? ""),
		[contacts, contact?.id],
	)

	const handleSelect = (target: { id: string }) => {
		if (!contact) return
		openMergeContactDialog(target.id)
		analytics.track("open-merge-contact-dialog", {
			cmdk: true,
			id: contact.id,
			withId: target.id,
		})
	}

	if (!contact) {
		return (
			<CommandGroup heading="Merge person">
				<div className="px-2 py-3 text-sm text-secondary">
					Open a person first to merge them with another one.
				</div>
			</CommandGroup>
		)
	}

	const heading = mergeContactLabel(contact.name || contact.email)

	if (targets.length === 0) {
		return (
			<CommandGroup heading={heading}>
				<div className="px-2 py-3 text-sm text-secondary">
					{mergeContactEmptyMessage(commandText)}
				</div>
			</CommandGroup>
		)
	}

	return (
		<CommandGroup heading={heading}>
			{targets.map((target) => (
				<CommandItem
					size="lg"
					key={target.id}
					value={`${target.name ?? ""}-${target.email}-${target.id}`}
					Icon={() => (
						<Avatar
							size="xs"
							name={target.name ?? target.email}
							image={target.imageUrl}
						/>
					)}
					onSelect={() => handleSelect(target)}
				>
					{target.name || target.email}
				</CommandItem>
			))}
		</CommandGroup>
	)
})

const useCurrentCompany = () => {
	const { pathname } = useLocation()
	const params = useParams({ strict: false })
	const isCompanyRoute = pathname.startsWith("/customers/") && !!params.id
	const [company] = useQuery(
		queries.companyDetails(isCompanyRoute && params.id ? params.id : ""),
		{ ttl: "none" },
	)
	return isCompanyRoute ? company : undefined
}

const CompanyContextualActions = memo(() => {
	const { addPage } = useCmdkState()
	const company = useCurrentCompany()

	if (!company) {
		return null
	}

	return (
		<CommandGroup heading="Company Actions">
			<RoleCheck not={["VIEWER"]}>
				<CommandItem
					size="lg"
					Icon={ArrowsLeftRight}
					onSelect={() => addPage("Merge company")}
				>
					{mergeCompanyLabel(company.name)}
				</CommandItem>
			</RoleCheck>
		</CommandGroup>
	)
})

const MergeCompanyOptions = memo(() => {
	const { openMergeCompanyDialog, commandText } = useCmdkState()
	const company = useCurrentCompany()

	const [companies] = useQuery(
		queries.searchCompanies({ query: commandText, limit: 20 }),
		{ ttl: "none" },
	)
	const targets = useMemo(
		() => mergeCompanyTargets(companies, company?.id ?? ""),
		[companies, company?.id],
	)

	const handleSelect = (target: { id: string }) => {
		if (!company) return
		openMergeCompanyDialog(target.id)
		analytics.track("open-merge-company-dialog", {
			cmdk: true,
			id: company.id,
			withId: target.id,
		})
	}

	if (!company) {
		return (
			<CommandGroup heading="Merge company">
				<div className="px-2 py-3 text-sm text-secondary">
					Open a company first to merge it with another one.
				</div>
			</CommandGroup>
		)
	}

	if (targets.length === 0) {
		return (
			<CommandGroup heading={mergeCompanyLabel(company.name)}>
				<div className="px-2 py-3 text-sm text-secondary">
					{mergeCompanyEmptyMessage(commandText)}
				</div>
			</CommandGroup>
		)
	}

	return (
		<CommandGroup heading={mergeCompanyLabel(company.name)}>
			{targets.map((target) => (
				<CommandItem
					size="lg"
					key={target.id}
					value={`${target.name}-${target.id}`}
					Icon={() => (
						<CompanyLogo
							brandfetchData={
								target.companyMeta?.brandfetchData as unknown as BrandfetchData
							}
							fallback={target.name}
							linearLogoUrl={target.logoUrl || undefined}
							size="xs"
						/>
					)}
					onSelect={() => handleSelect(target)}
				>
					{target.name}
				</CommandItem>
			))}
		</CommandGroup>
	)
})

// cmd+k mirrors the settings sidebar, so both lists come from it. Only the icon
// differs per surface: the sidebar uses outline glyphs, cmd+k the fill variant.
// A page with no entry here falls back to its outline icon.
const SETTINGS_FILL_ICONS: Record<string, NavItem["icon"]> = {
	"/settings/profile": UserSettings,
	"/settings/general": Office3,
	"/settings/plans": CreditCard,
	"/settings/plans/pricing": Layers2,
	"/settings/plans/invoices": Receipt,
	"/settings/members": Users,
	"/settings/transfer": Import,
	"/settings/csv-import": Table,
	"/settings/productboard-import": Import,
	"/settings/contacts-csv-import": AddressBook,
	"/settings/changelog-csv-import": StackPerspective,
	"/settings/docs-csv-import": BookOpen,
	"/settings/docs-markdown-import": Markdown,
	"/settings/attachment-import": Paperclip,
	"/settings/integrations/api": Code2,
	"/settings/integrations": Shapes2,
	"/settings/integrations/postgres": DatabaseZap,
	"/settings/notifications": NotificationIcon,
	"/settings/notifications/inbox": IconInboxFill18,
	"/settings/notifications/email": Mail,
	"/settings/mcp": SquaresConnected,
	"/settings/ai": Bot,
	"/settings/ai/usage": ChartActivity,
	"/settings/ai/agent": OrbitSparkle,
	"/settings/ai/close-loop": ArrowRotateCheck,
	"/settings/ai/routines": BoltLightning,
	"/settings/ai/drafts": MessageSparkle,
	"/settings/docs": BookOpen,
	"/settings/roadmap": Cube,
	"/settings/changelog": StackPerspective,
	"/settings/support-portal": LifeBuoy,
	"/settings/widget": Msgs,
	"/settings/portal": WindowUser,
	"/settings/portal/theme": ThemeIcon,
	"/settings/portal/domain": LinkIcon,
	"/settings/portal/sso": Fingerprint,
	"/settings/portal/instances": Window2,
	"/settings/emails": MailPlus,
	"/settings/emails/setup": Mail,
	"/settings/emails/blocklist": MailX,
	"/settings/emails/forwarding": CornerUpLeft,
	"/settings/office-hours": Fire,
	"/settings/languages": Language,
	"/settings/custom-connectors": ConnectedDots,
	"/settings/custom-connectors/history": History,
	"/settings/macros": Quote,
	"/settings/assignments": UserSettings,
	"/settings/tags": Tag,
}

const withFillIcon = (item: NavItem): NavItem => ({
	...item,
	icon: SETTINGS_FILL_ICONS[item.href] ?? item.icon,
})

const SETTINGS_NAV_ITEMS: Array<NavItem> =
	ALL_SETTINGS_NAV_ITEMS.map(withFillIcon)

const SETTINGS_SUBPAGE_NAV_ITEMS: Array<NavItem> =
	ALL_SETTINGS_SUBPAGE_NAV_ITEMS.map(withFillIcon)

const NavigationItems = memo(() => {
	const navigate = useNavigate()
	const { setIsOpen } = useCmdkState()
	const { data: workspaceInfo } = api.workspace.info.useQuery(undefined, {
		staleTime: 60_000,
	})
	const cursorConnected = !!workspaceInfo?.cursorConnected
	const sentryAvailable = useSentryAvailable()
	const settingsItems = useMemo<Array<NavItem>>(
		() =>
			visibleSettingsNavItems(
				[...SETTINGS_NAV_ITEMS, ...SETTINGS_SUBPAGE_NAV_ITEMS],
				{ cursorConnected, sentryAvailable },
			),
		[cursorConnected, sentryAvailable],
	)
	const items = useMemo<Array<NavItem>>(() => {
		return [
			{
				href: "/notifications",
				title: "Notifications",
				icon: NotificationIcon,
				shortcut: ["g", "n"],
			},
			...NAV_ITEMS,
			{
				href: "/settings/general",
				title: "Settings",
				icon: Settings,
				shortcut: ["g", "s"],
			},
			{
				href: "/search",
				title: "Search",
				icon: Search,
				shortcut: ["/"],
			},
		].reduce<NavItem[]>(removeDuplicates, [])
	}, [])

	const navigateAndClose = (href: string) => {
		void navigate({ href: href })
		setIsOpen(false)
		analytics.track("cmdk-navigation", { href, cmdk: true })
	}

	return (
		<>
			<CommandGroup heading="Navigation">
				{items.map((item) => (
					<CommandItem
						size="lg"
						key={item.href}
						onSelect={() => navigateAndClose(item.href)}
						Icon={item.icon}
						shortcuts={item.shortcut}
					>
						{item.title}
					</CommandItem>
				))}
			</CommandGroup>
			<CommandGroup heading="Settings">
				{settingsItems.map((item) => (
					<CommandItem
						size="lg"
						key={item.href}
						value={`settings ${item.title} ${item.href}`}
						keywords={item.keywords ? item.keywords.split(" ") : undefined}
						onSelect={() => navigateAndClose(item.href)}
						Icon={item.icon}
					>
						{item.title}
					</CommandItem>
				))}
			</CommandGroup>
		</>
	)
})

const SlaOptions = memo(() => {
	const { pathname } = useLocation()
	const params = useParams({ strict: false })
	const { setIsOpen, removePage, setCommandText, commandText } = useCmdkState()
	const zero = useZero()
	const workspace = useZeroWorkspace()

	const isThreadRoute = pathname.startsWith("/threads/") && params.id
	const [thread] = useQuery(
		isThreadRoute && params.id
			? queries.threadWithFullDetails(threadDetailQueryArgs(params.id))
			: queries.threadWithFullDetails(threadDetailQueryArgs("")),
		{ ttl: "none" },
	)

	const hasSla = useMemo(
		() => computeHasSla(thread, workspace),
		[thread, workspace],
	)

	const suggestions = useMemo(
		() => getSlaSuggestionsForInput(commandText),
		[commandText],
	)

	const handleSelectDate = async (date: Date) => {
		if (!thread) return
		await mutateOk(
			zero.mutate(
				mutators.threads.update({
					id: thread.id,
					slaDeadlineAtIso: date.toISOString(),
				}),
			),
		)
		toast.success({ title: "SLA updated" })
		setCommandText("")
		removePage()
		setIsOpen(false)
	}

	const handleRemoveSla = async () => {
		if (!thread) return
		await mutateOk(
			zero.mutate(
				mutators.threads.update({
					id: thread.id,
					slaDeadlineAtIso: new Date(0).toISOString(),
				}),
			),
		)
		toast.success({ title: "SLA removed" })
		setCommandText("")
		removePage()
		setIsOpen(false)
	}

	const handleSelectPreset = async (minutes: number) => {
		const deadline = new Date(Date.now() + minutes * 60 * 1000)
		await handleSelectDate(deadline)
	}

	const showDefaults = suggestions.length === 0

	return (
		<CommandGroup>
			{showDefaults
				? SLA_DURATIONS.map((opt) => (
						<CommandItem
							size="lg"
							key={opt.minutes}
							onSelect={() => handleSelectPreset(opt.minutes)}
						>
							<div className="flex w-full items-center justify-between">
								<span>{opt.label}</span>
								<span className="text-muted-foreground text-xs">
									{format(
										new Date(Date.now() + opt.minutes * 60 * 1000),
										"EEE, d MMM, HH:mm",
									)}
								</span>
							</div>
						</CommandItem>
					))
				: suggestions.map((suggestion) => (
						<CommandItem
							size="lg"
							key={suggestion.label}
							value={suggestion.label}
							onSelect={() => handleSelectDate(suggestion.date)}
						>
							<div className="flex w-full items-center justify-between">
								<span>{suggestion.label}</span>
								<span className="text-muted-foreground text-xs">
									{format(suggestion.date, "EEE, d MMM, HH:mm")}
								</span>
							</div>
						</CommandItem>
					))}
			{hasSla && (
				<CommandItem size="lg" onSelect={handleRemoveSla}>
					Remove SLA
				</CommandItem>
			)}
		</CommandGroup>
	)
})

const MergeThreadOptions = memo(() => {
	const { pathname } = useLocation()
	const params = useParams({ strict: false })
	const { setIsOpen, removePage, setCommandText, commandText } = useCmdkState()
	const navigate = useNavigate()
	const mergeMutation = api.thread.merge.useMutation()

	const isThreadRoute = pathname.startsWith("/threads/") && params.id
	const sourceThreadId = isThreadRoute && params.id ? params.id : ""

	const [sourceThread] = useQuery(queries.threadByID(sourceThreadId || ""), {
		ttl: "none",
	})
	const contactId = sourceThread?.contactId ?? undefined

	const [threads] = useQuery(
		queries.searchThreads({
			query: commandText,
			limit: 20,
			excludeId: sourceThreadId || undefined,
			contactId,
		}),
		{ ttl: "none" },
	)

	const handleSelect = async (target: { id: string; title: string | null }) => {
		if (!sourceThreadId) return
		const targetTitle = target.title?.trim() || "thread"
		setCommandText("")
		removePage()
		setIsOpen(false)

		try {
			await mergeMutation.mutateAsync({
				sourceId: sourceThreadId,
				targetId: target.id,
			})
			// Navigate only once the merge landed, so a failure leaves the user on
			// the source thread instead of on a target that swallowed nothing.
			void navigate({
				to: "/threads/$id",
				params: { id: target.id },
				replace: true,
			})
			analytics.track("merge-thread", {
				cmdk: true,
				sourceId: sourceThreadId,
				targetId: target.id,
			})
			toast.success({
				title: "Threads merged",
				descriptionAction: {
					label: `Merged into ${targetTitle}`,
					callback: () =>
						void navigate({ to: "/threads/$id", params: { id: target.id } }),
				},
				icon: ArrowsLeftRight,
			})
		} catch (error) {
			toast.error({
				title: "Failed to merge",
				description:
					error instanceof Error ? error.message : "Something went wrong.",
			})
		}
	}

	if (sourceThread && !contactId) {
		return (
			<CommandGroup heading="Merge into">
				<div className="px-2 py-3 text-sm text-secondary">
					This thread has no contact. Add a contact first; you can only merge
					into another thread from the same person.
				</div>
			</CommandGroup>
		)
	}

	if (contactId && threads.length === 0) {
		return (
			<CommandGroup heading="Threads from this contact">
				<div className="px-2 py-3 text-sm text-secondary">
					{commandText.trim()
						? "No matching threads from this contact. Only threads from the same person are shown."
						: "No other threads from this contact. You can only merge into a thread from the same person."}
				</div>
			</CommandGroup>
		)
	}

	return (
		<CommandGroup heading="Threads from this contact">
			{threads.map((thread) => (
				<CommandPrimitive.Item
					key={thread.id}
					value={`${thread.title ?? "Untitled"}-${thread.id}`}
					onSelect={() => void handleSelect(thread)}
					className={cn(
						"relative cursor-default select-none rounded-md px-1 py-0 outline-none",
						"aria-disabled:pointer-events-none aria-disabled:opacity-50",
						"aria-selected:bg-gray-100 dark:aria-selected:bg-gray-800",
					)}
				>
					<ThreadListItem thread={thread} teamMembers={[]} compact rounded />
				</CommandPrimitive.Item>
			))}
		</CommandGroup>
	)
})

const SpawnAgentIssueList = memo(() => {
	const { pathname } = useLocation()
	const params = useParams({ strict: false })
	const { setIsOpen, removePage, setCommandText } = useCmdkState()
	const navigate = useNavigate()

	const isThreadRoute = pathname.startsWith("/threads/") && params.id
	const [thread] = useQuery(
		isThreadRoute && params.id
			? queries.threadWithFullDetails(threadDetailQueryArgs(params.id))
			: queries.threadWithFullDetails(threadDetailQueryArgs("")),
		{ ttl: "none" },
	)

	const triggerAgent = api.codingAgent.trigger.useMutation()

	const handleSelectIssue = async (issue: {
		id: string
		title: string
		identifier: string | null
	}) => {
		try {
			await triggerAgent.mutateAsync({
				issueId: issue.id,
				triggerSource: "cmdk",
			})
			toast.success({
				title: "Productlane Agent spawned",
				description: `Working on ${issue.identifier ?? issue.title}`,
			})
			setCommandText("")
			removePage()
			setIsOpen(false)
			analytics.track("spawn-coding-agent", {
				cmdk: true,
				threadId: thread?.id,
				issueId: issue.id,
			})
		} catch (error) {
			if (
				error instanceof Error &&
				"data" in error &&
				(error as { data?: { code?: string } }).data?.code ===
					"PRECONDITION_FAILED"
			) {
				toast.error({
					title: "Cursor Cloud not configured",
					description: error.message,
					action: {
						label: "Configure",
						callback: () => {
							void navigate({ to: "/settings/integrations/cursor" })
							setIsOpen(false)
						},
					},
				})
			} else {
				toast.error({
					title: "Failed to spawn Productlane Agent",
					description: error instanceof Error ? error.message : "Unknown error",
				})
			}
		}
	}

	const issues = (thread?.issues ?? []).filter((i) => !i.completedAt)

	return (
		<CommandGroup heading="Select issue for Productlane Agent">
			{issues.map((issue) => (
				<CommandItem
					size="lg"
					key={issue.id}
					value={`${issue.title}-${issue.id}`}
					Icon={() => (
						<IssueStatusIcon
							className="h-3.5 w-3.5"
							status={issue.status}
							color={issue.statusColor ?? ""}
						/>
					)}
					onSelect={() => void handleSelectIssue(issue)}
				>
					{issue.identifier && (
						<span className="mr-2 text-xs text-gray-500">
							{issue.identifier}
						</span>
					)}
					{issue.title}
				</CommandItem>
			))}
		</CommandGroup>
	)
})

const OpenInClaudeCodeIssueList = memo(() => {
	const { pathname } = useLocation()
	const params = useParams({ strict: false })
	const { setIsOpen, removePage, setCommandText } = useCmdkState()
	const navigate = useNavigate()

	const isThreadRoute = pathname.startsWith("/threads/") && params.id
	const [thread] = useQuery(
		isThreadRoute && params.id
			? queries.threadWithFullDetails(threadDetailQueryArgs(params.id))
			: queries.threadWithFullDetails(threadDetailQueryArgs("")),
		{ ttl: "none" },
	)

	const utils = api.useUtils()

	const handleSelectIssue = async (issue: {
		id: string
		title: string
		identifier: string | null
	}) => {
		try {
			const { deepLink, branchName, prompt } =
				await utils.codingAgent.buildClaudeCodeLink.fetch({
					issueId: issue.id,
				})
			openDeepLink(deepLink)
			toast.success({
				title: "Opening in Claude Code",
				description: branchName ? `Branch ${branchName}` : undefined,
				descriptionAction: {
					label: "Copy prompt",
					callback: () => void navigator.clipboard.writeText(prompt),
				},
			})
			setCommandText("")
			removePage()
			setIsOpen(false)
			analytics.track("open-issue-in-claude-code", {
				cmdk: true,
				threadId: thread?.id,
				issueId: issue.id,
			})
		} catch (error) {
			if (
				error instanceof Error &&
				"data" in error &&
				(error as { data?: { code?: string } }).data?.code ===
					"PRECONDITION_FAILED"
			) {
				toast.error({
					title: "No repository configured",
					description: error.message,
					action: {
						label: "Configure",
						callback: () => {
							void navigate({ to: "/settings/integrations/cursor" })
							setIsOpen(false)
						},
					},
				})
			} else {
				toast.error({
					title: "Failed to open in Claude Code",
					description: error instanceof Error ? error.message : "Unknown error",
				})
			}
		}
	}

	const issues = (thread?.issues ?? []).filter((i) => !i.completedAt)

	return (
		<CommandGroup heading="Open issue in Claude Code">
			{issues.map((issue) => (
				<CommandItem
					size="lg"
					key={issue.id}
					value={`${issue.title}-${issue.id}`}
					Icon={() => (
						<IssueStatusIcon
							className="h-3.5 w-3.5"
							status={issue.status}
							color={issue.statusColor ?? ""}
						/>
					)}
					onSelect={() => void handleSelectIssue(issue)}
				>
					{issue.identifier && (
						<span className="mr-2 text-xs text-gray-500">
							{issue.identifier}
						</span>
					)}
					{issue.title}
				</CommandItem>
			))}
		</CommandGroup>
	)
})

const GenerateDocsIssueList = memo(() => {
	const { pathname } = useLocation()
	const params = useParams({ strict: false })
	const { setIsOpen, removePage, setCommandText } = useCmdkState()

	const isThreadRoute = pathname.startsWith("/threads/") && params.id
	const [thread] = useQuery(
		isThreadRoute && params.id
			? queries.threadWithFullDetails(threadDetailQueryArgs(params.id))
			: queries.threadWithFullDetails(threadDetailQueryArgs("")),
		{ ttl: "none" },
	)

	const generateDocsProposalsMutation =
		api.ai.generateDocsProposals.useMutation()

	const handleSelectIssue = async (issue: {
		id: string
		title: string
		identifier: string | null
	}) => {
		if (!thread) return
		try {
			await generateDocsProposalsMutation.mutateAsync({
				issueIds: [issue.id],
				threadId: thread.id,
			})
			toast.success({
				title: "Documentation proposal started",
				description: `Working on docs for ${issue.identifier ?? issue.title}`,
			})
			setCommandText("")
			removePage()
			setIsOpen(false)
			analytics.track("docs-proposals-generate", {
				cmdk: true,
				threadId: thread.id,
				issueId: issue.id,
			})
		} catch (error) {
			toast.error({
				title: "Failed to start documentation proposal",
				description: error instanceof Error ? error.message : "Unknown error",
			})
		}
	}

	const issues = thread?.issues ?? []

	return (
		<CommandGroup heading="Select issue for documentation proposal">
			{issues.map((issue) => (
				<CommandItem
					size="lg"
					key={issue.id}
					value={`${issue.title}-${issue.id}`}
					Icon={() => (
						<IssueStatusIcon
							className="h-3.5 w-3.5"
							status={issue.status}
							color={issue.statusColor ?? ""}
						/>
					)}
					onSelect={() => void handleSelectIssue(issue)}
				>
					{issue.identifier && (
						<span className="mr-2 text-xs text-gray-500">
							{issue.identifier}
						</span>
					)}
					{issue.title}
				</CommandItem>
			))}
		</CommandGroup>
	)
})

function NotificationContextualActions() {
	const { setIsOpen } = useCmdkState()
	const commands = useNotificationCommands((s) => s.commands)
	const openSnooze = useSnoozeOpener((s) => s.open)
	if (!commands) return null
	const run = (action: () => void) => () => {
		setIsOpen(false)
		action()
	}
	const { actions } = notificationCopy
	return (
		<>
			{commands.current && (
				<CommandGroup heading="Notification">
					<CommandItem
						size="lg"
						Icon={commands.current.unread ? InboxCheckIcon : InboxDotIcon}
						shortcuts={[ShortcutSymbols.U]}
						onSelect={run(commands.toggleRead)}
					>
						{commands.current.unread ? actions.markRead : actions.markUnread}
					</CommandItem>
					{openSnooze && (
						<CommandItem
							size="lg"
							Icon={IconClockSnoozeOutline18}
							shortcuts={[ShortcutSymbols.H]}
							onSelect={run(openSnooze)}
						>
							{actions.snooze}
						</CommandItem>
					)}
					<CommandItem
						size="lg"
						Icon={InboxXmarkIcon}
						shortcuts={[ShortcutSymbols.BACKSPACE]}
						onSelect={run(commands.remove)}
					>
						{actions.deleteNotification}
					</CommandItem>
				</CommandGroup>
			)}
			<CommandGroup heading="Notifications">
				<CommandItem
					size="lg"
					Icon={InboxCheckIcon}
					shortcuts={[ShortcutSymbols.ALT, ShortcutSymbols.U]}
					onSelect={run(commands.markAllRead)}
				>
					{actions.markAllRead}
				</CommandItem>
				<CommandItem
					size="lg"
					Icon={InboxXmarkIcon}
					shortcuts={[ShortcutSymbols.SHIFT, ShortcutSymbols.BACKSPACE]}
					onSelect={run(commands.deleteRead)}
				>
					{actions.deleteRead}
				</CommandItem>
				<CommandItem
					size="lg"
					Icon={InboxXmarkIcon}
					onSelect={run(commands.deleteAll)}
				>
					{actions.deleteAll}
				</CommandItem>
				<CommandItem
					size="lg"
					Icon={InboxXmarkIcon}
					onSelect={run(commands.deleteCompleted)}
				>
					{actions.deleteCompleted}
				</CommandItem>
			</CommandGroup>
		</>
	)
}
